import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ShoppingBag, CheckSquare, Square } from 'lucide-react';
const shoppingList = [{
  category: 'Vegetales y Frutas',
  items: ['Espinacas / Acelgas', 'Brócoli / Coliflor', 'Calabacín', 'Aguacates (¡muchos!)', 'Limones', 'Frutos rojos (arándanos, fresas)']
}, {
  category: 'Proteínas',
  items: ['Huevos orgánicos o de pastoreo', 'Salmón salvaje o sardinas', 'Pechuga de pollo', 'Caldo de huesos (o huesos para hacerlo)']
}, {
  category: 'Despensa Dorada',
  items: ['Aceite de oliva extra virgen', 'Aceite de coco o Ghee', 'Cúrcuma en polvo', 'Jengibre (fresco y en polvo)', 'Canela de Ceilán', 'Leche de almendras (sin azúcar)']
}];
const faqs = [{
  q: '¿Puedo tomar café durante los 14 días?',
  a: 'Sí, pero con moderación (1-2 tazas máximo) y siempre por la mañana. Evita el azúcar y los lácteos convencionales. Puedes agregarle un poco de leche de almendras o aceite de coco.'
}, {
  q: '¿Qué hago si siento mucha hambre entre comidas?',
  a: 'Si sientes hambre real, bebe primero un gran vaso de agua o infusión. Si persiste, come un puñado pequeño de nueces o almendras, o medio aguacate con sal. El objetivo no es pasar hambre.'
}, {
  q: 'Tomo medicamentos para la presión/tiroides, ¿puedo hacer esto?',
  a: 'Este es un protocolo de alimentación natural y saludable, no interfiere con la mayoría de los medicamentos. Sin embargo, siempre es recomendable consultar con tu médico antes de hacer cambios drásticos en tu dieta.'
}, {
  q: '¿Puedo hacer ejercicio durante el protocolo?',
  a: 'Sí, pero prioriza ejercicios suaves y restaurativos como caminatas ligeras, yoga, pilates o natación. Evita el cardio intenso que pueda elevar tu cortisol mientras tu cuerpo se está desinflamando.'
}, {
  q: '¿Qué pasa después de los 14 días?',
  a: 'Notarás menos hinchazón, más energía y menos dolores. La idea es que mantengas los principios básicos (Regla del Plato, evitar los 5 ladrones) como un estilo de vida, permitiéndote flexibilidades ocasionales.'
}];
export function AnnexSection() {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const toggleItem = (item: string) => {
    const newChecked = new Set(checkedItems);
    if (newChecked.has(item)) {
      newChecked.delete(item);
    } else {
      newChecked.add(item);
    }
    setCheckedItems(newChecked);
  };
  return <section id="anexos" className="min-h-screen py-24 px-6 lg:px-16 bg-white relative" data-id="element-12">
      <div className="max-w-4xl mx-auto" data-id="element-13">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16 text-center" data-id="element-14">
          <h2 className="text-gold font-medium tracking-widest uppercase text-sm mb-4" data-id="element-15">
            Anexos
          </h2>
          <h3 className="font-serif text-4xl lg:text-5xl text-darkBrown font-bold mb-6 leading-tight" data-id="element-16">
            Recursos Finales
          </h3>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-24" data-id="element-17">
          {/* Shopping List */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-lightGold p-8 rounded-3xl border border-gold/20" data-id="element-18">
            <div className="flex items-center gap-3 mb-6" data-id="element-19">
              <ShoppingBag className="w-6 h-6 text-gold" data-id="element-20" />
              <h4 className="font-serif text-2xl font-bold text-darkBrown" data-id="element-21">
                Lista de Compras
              </h4>
            </div>

            <div className="space-y-6" data-id="element-22">
              {shoppingList.map((section, idx) => <div key={idx} data-id="element-23">
                  <h5 className="font-bold text-darkBrown/80 mb-3 text-sm uppercase tracking-wider border-b border-gold/20 pb-2" data-id="element-24">
                    {section.category}
                  </h5>
                  <ul className="space-y-2" data-id="element-25">
                    {section.items.map((item, itemIdx) => {
                  const isChecked = checkedItems.has(item);
                  return <li key={itemIdx} data-id="element-26">
                          <button onClick={() => toggleItem(item)} className="flex items-center gap-3 text-left w-full group" data-id="element-27">
                            {isChecked ? <CheckSquare className="w-5 h-5 text-gold flex-shrink-0" data-id="element-28" /> : <Square className="w-5 h-5 text-darkBrown/30 group-hover:text-gold/50 flex-shrink-0 transition-colors" data-id="element-29" />}
                            <span className={`text-base transition-colors ${isChecked ? 'text-darkBrown/40 line-through' : 'text-darkBrown'}`} data-id="element-30">
                              {item}
                            </span>
                          </button>
                        </li>;
                })}
                  </ul>
                </div>)}
            </div>
          </motion.div>

          {/* FAQs */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} data-id="element-31">
            <h4 className="font-serif text-2xl font-bold text-darkBrown mb-6" data-id="element-32">
              Preguntas Frecuentes
            </h4>
            <div className="space-y-4" data-id="element-33">
              {faqs.map((faq, idx) => <div key={idx} className="border border-gold/20 rounded-xl overflow-hidden bg-cream/30" data-id="element-34">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full px-6 py-4 text-left flex justify-between items-center bg-white hover:bg-lightGold/50 transition-colors" data-id="element-35">
                    <span className="font-medium text-darkBrown pr-4" data-id="element-36">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gold transition-transform ${openFaq === idx ? 'rotate-180' : ''}`} data-id="element-37" />
                  </button>
                  <AnimatePresence data-id="element-38">
                    {openFaq === idx && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-39">
                        <div className="px-6 py-4 text-darkBrown/80 text-sm border-t border-gold/10" data-id="element-40">
                          {faq.a}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Closing Quote */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} className="text-center max-w-2xl mx-auto py-12 border-t border-gold/20" data-id="element-41">
          <p className="font-serif text-2xl lg:text-3xl text-darkBrown italic mb-6 leading-relaxed" data-id="element-42">
            "Tu cuerpo no te está fallando, simplemente te está pidiendo que lo
            cuides de una manera diferente. Nunca es tarde para sanar."
          </p>
          <div className="w-12 h-1 bg-gold mx-auto rounded-full" data-id="element-43"></div>
        </motion.div>
      </div>
    </section>;
}